import { create } from 'zustand';
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  setDoc,
  where,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  COACHING_APPOINTMENTS_COLLECTION,
  type CoachingAppointment,
} from '../types/coachingAppointment';
import { generateSimpleId } from './utils';
import { normalizeExternalMeetUrl } from '../utils/openGoogleMeet';

const COLLECTION = COACHING_APPOINTMENTS_COLLECTION;

const normalizeAppointment = (id: string, raw: Record<string, unknown>): CoachingAppointment | null => {
  const clientId = String(raw.clientId || '').trim();
  const title = String(raw.title || '').trim();
  const datum = String(raw.datum || '').trim();
  const uhrzeit = String(raw.uhrzeit || '').trim();
  if (!clientId || !title || !datum) return null;

  const dauerRaw = Number(raw.dauerMinuten);
  return {
    id,
    clientId,
    title,
    datum,
    uhrzeit: uhrzeit || '10:00',
    dauerMinuten: Number.isFinite(dauerRaw) && dauerRaw > 0 ? dauerRaw : undefined,
    meetingLink: normalizeExternalMeetUrl(String(raw.meetingLink || '')),
    coachName: String(raw.coachName || '').trim() || 'Coach',
    coachAvatarUrl: raw.coachAvatarUrl ? String(raw.coachAvatarUrl).trim() : undefined,
    coachId: raw.coachId ? String(raw.coachId).trim() : undefined,
    notizen: raw.notizen ? String(raw.notizen).trim() : undefined,
    createdAt: String(raw.createdAt || new Date().toISOString()),
    updatedAt: raw.updatedAt ? String(raw.updatedAt) : undefined,
  };
};

const sortByDatumAsc = (a: CoachingAppointment, b: CoachingAppointment) => {
  const ka = `${a.datum}T${a.uhrzeit || '00:00'}`;
  const kb = `${b.datum}T${b.uhrzeit || '00:00'}`;
  return ka.localeCompare(kb);
};

export type CoachingAppointmentInput = Omit<CoachingAppointment, 'id' | 'createdAt' | 'updatedAt'> & {
  id?: string;
  createdAt?: string;
};

interface CoachingAppointmentState {
  appointments: CoachingAppointment[];
  isLoading: boolean;
  error: string | null;
  fetchByClientId: (clientId: string) => Promise<CoachingAppointment[]>;
  upsertAppointment: (input: CoachingAppointmentInput) => Promise<CoachingAppointment>;
  deleteAppointment: (id: string) => Promise<void>;
  clear: () => void;
}

const useCoachingAppointmentStore = create<CoachingAppointmentState>((set, get) => ({
  appointments: [],
  isLoading: false,
  error: null,

  clear: () => set({ appointments: [], error: null }),

  fetchByClientId: async (clientId: string) => {
    const uid = String(clientId || '').trim();
    if (!uid) {
      set({ appointments: [], isLoading: false });
      return [];
    }
    set({ isLoading: true, error: null });
    try {
      const q = query(collection(db, COLLECTION), where('clientId', '==', uid));
      const snap = await getDocs(q);
      const list: CoachingAppointment[] = [];
      snap.forEach((d) => {
        const item = normalizeAppointment(d.id, d.data() as Record<string, unknown>);
        if (item) list.push(item);
      });
      list.sort(sortByDatumAsc);
      set({ appointments: list, isLoading: false });
      return list;
    } catch (e: any) {
      console.error('[coachingAppointmentStore] fetchByClientId failed', e);
      set({
        isLoading: false,
        error: e?.message || 'Termine konnten nicht geladen werden.',
      });
      return get().appointments;
    }
  },

  upsertAppointment: async (input) => {
    const id = String(input.id || generateSimpleId()).trim();
    const now = new Date().toISOString();
    const existing = get().appointments.find((a) => a.id === id);
    const dauerRaw = Number(input.dauerMinuten);
    const dauerMinuten =
      Number.isFinite(dauerRaw) && dauerRaw > 0 ? dauerRaw : undefined;
    const coachAvatarUrl = String(input.coachAvatarUrl || '').trim() || undefined;
    const coachId = String(input.coachId || '').trim() || undefined;
    const notizen = String(input.notizen || '').trim() || undefined;

    const payload: CoachingAppointment = {
      id,
      clientId: String(input.clientId || '').trim(),
      title: String(input.title || '').trim(),
      datum: String(input.datum || '').trim(),
      uhrzeit: String(input.uhrzeit || '10:00').trim(),
      meetingLink: normalizeExternalMeetUrl(String(input.meetingLink || '')),
      coachName: String(input.coachName || '').trim() || 'Coach',
      createdAt: existing?.createdAt || input.createdAt || now,
      updatedAt: now,
      ...(dauerMinuten !== undefined ? { dauerMinuten } : {}),
      ...(coachAvatarUrl ? { coachAvatarUrl } : {}),
      ...(coachId ? { coachId } : {}),
      ...(notizen ? { notizen } : {}),
    };

    if (!payload.clientId || !payload.title || !payload.datum) {
      throw new Error('clientId, title und datum sind Pflichtfelder.');
    }

    // Firestore verbietet undefined — nur gesetzte Felder schreiben
    const firestoreData: Record<string, unknown> = {
      clientId: payload.clientId,
      title: payload.title,
      datum: payload.datum,
      uhrzeit: payload.uhrzeit,
      meetingLink: payload.meetingLink,
      coachName: payload.coachName,
      createdAt: payload.createdAt,
      updatedAt: payload.updatedAt,
    };
    if (payload.dauerMinuten !== undefined) firestoreData.dauerMinuten = payload.dauerMinuten;
    if (payload.coachAvatarUrl) firestoreData.coachAvatarUrl = payload.coachAvatarUrl;
    if (payload.coachId) firestoreData.coachId = payload.coachId;
    if (payload.notizen) firestoreData.notizen = payload.notizen;

    await setDoc(doc(db, COLLECTION, id), firestoreData, { merge: true });

    const next = [
      ...get().appointments.filter((a) => a.id !== id),
      payload,
    ].sort(sortByDatumAsc);
    set({ appointments: next, error: null });
    return payload;
  },

  deleteAppointment: async (id: string) => {
    const aid = String(id || '').trim();
    if (!aid) return;
    await deleteDoc(doc(db, COLLECTION, aid));
    set({
      appointments: get().appointments.filter((a) => a.id !== aid),
      error: null,
    });
  },
}));

export default useCoachingAppointmentStore;

/** Datum+Uhrzeit → lokales Date (ohne TZ-Drift). */
export const parseCoachingLocalDateTime = (datum: string, uhrzeit?: string): Date | null => {
  const d = String(datum || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return null;
  const [y, m, day] = d.split('-').map(Number);
  const time = String(uhrzeit || '00:00').trim();
  const match = time.match(/^(\d{1,2}):(\d{2})$/);
  const hh = match ? Number(match[1]) : 0;
  const mm = match ? Number(match[2]) : 0;
  const dt = new Date(y, m - 1, day, hh, mm, 0, 0);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

export const isCoachingAppointmentUpcoming = (ap: CoachingAppointment, now = new Date()): boolean => {
  const dt = parseCoachingLocalDateTime(ap.datum, ap.uhrzeit);
  if (!dt) return false;
  const end = new Date(dt);
  end.setMinutes(end.getMinutes() + (ap.dauerMinuten && ap.dauerMinuten > 0 ? ap.dauerMinuten : 60));
  return end >= now;
};
